import React from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { LazyQueryExecFunction } from '@apollo/client/react';
import { SiteAreasSearchListQuery } from 'graphql-common';
import {
  getOptionValidation,
  getOptionsArrayValidation,
} from '@lib/utils/validationUtils';
import { ButtonSizes } from '@lib/components/Button/Button';
import Select from '@lib/components/Select/Select';
import {
  FieldChangeCallBackArgs,
  FieldItem,
  Values,
} from '@lib/interfaces/form';
import { getSiteAreasLoadOptions } from 'utils/fetch/searchLists/siteAreasSearchList';
import formStyles from '@lib/assets/styles/Form.module.scss';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { GetLoadOptions } from '@lib/components/Select/types';
import SiteOption from '@lib/components/Select/components/SiteOption';
import ChipSingleValue from '@lib/components/Select/components/ChipSingleValue';
import SiteAndAreaFieldOption from '../SiteAndAreaFieldOption';
import SiteAndAreaOptionsAddItemComponent from '../SiteAndAreaOptionsAddItemComponent';

export const TASKS_OVERVIEW_FILTER_EMPTY_VALUES = {
  [TasksFilterFieldNames.Filter]: [
    {
      [TasksFilterFieldNames.Site]: null,
      [TasksFilterFieldNames.SiteAreaIds]: [],
    },
  ],
  [TasksFilterFieldNames.Period]: null,
};

type Props = {
  getSitesLoadOptions: GetLoadOptions;
  fetchSiteAreasSearchListLazyQuery: LazyQueryExecFunction<
    SiteAreasSearchListQuery,
    any
  >;
  closeFilter?: () => void;
};

export default function useFilterFields({
  getSitesLoadOptions,
  fetchSiteAreasSearchListLazyQuery,
  closeFilter,
}: Props): FieldItem[] {
  const { t } = useTranslation();
  return [
    {
      name: TasksFilterFieldNames.Filter,
      label: t('site-comma-area'),
      subFieldsEntity: 'site',
      fieldComponents: {
        FieldArrayAddItemComponent: (props) => (
          <SiteAndAreaOptionsAddItemComponent
            {...props}
            onCancel={closeFilter}
            buttonSize={ButtonSizes.small}
          />
        ),
        FieldArrayItemWrapperComponent: SiteAndAreaFieldOption,
      },
      subFieldsMinLength: 1,
      subFieldsMaxLength: 5,
      subFields: [
        {
          name: TasksFilterFieldNames.Site,
          label: t('site'),
          element: Select,
          validation: getOptionValidation({ t }),
          componentProps: {
            isAsync: true,
            getLoadOptions: getSitesLoadOptions,
            defaultOptions: false,
            usePortal: true,
            components: {
              Option: SiteOption,
              SingleValue: ChipSingleValue,
            },
          },
          fieldChangeCallBack: ({
            type,
            name,
            setValue,
          }: FieldChangeCallBackArgs) => {
            const nameArray = name?.split('.');
            const fieldName =
              name && nameArray ? nameArray[nameArray.length - 1] : name;
            if (
              type === 'change' &&
              name &&
              fieldName === TasksFilterFieldNames.Site
            ) {
              const siteAreaIdsName = name.replace(
                TasksFilterFieldNames.Site,
                TasksFilterFieldNames.SiteAreaIds,
              );
              setValue(siteAreaIdsName, []);
            }
          },
          isOptionalFn: () => false,
          hideErrorsSpace: true,
          formItemClassName: formStyles.formColumn,
        },
        {
          name: TasksFilterFieldNames.SiteAreaIds,
          label: t('areas-label'),
          element: Select,
          validation: getOptionsArrayValidation({ t }),
          componentProps: {
            isAsync: true,
            isMulti: true,
            usePortal: true,
            getLoadOptions: (name, values) => {
              const siteFieldName = name.replace(
                TasksFilterFieldNames.SiteAreaIds,
                `${TasksFilterFieldNames.Site}.value`,
              );
              const siteId = _get(values, siteFieldName, '') as string;
              return getSiteAreasLoadOptions(
                fetchSiteAreasSearchListLazyQuery,
                siteId,
              );
            },
            defaultOptions: false,
            disabledTooltipProps: {
              body: t('choose-site-first'),
            },
          },
          isDisabledFn: (values: Values, name?: string) => {
            const siteOptionName = name?.replace(
              TasksFilterFieldNames.SiteAreaIds,
              `${TasksFilterFieldNames.Site}.value`,
            );
            if (!siteOptionName) return false;
            const siteOption = _get(values, siteOptionName);
            return !siteOption;
          },
          getFieldKeyFn: (values: Values, name?: string) => {
            if (!name) return TasksFilterFieldNames.SiteAreaIds;
            const siteFieldName = name.replace(
              TasksFilterFieldNames.SiteAreaIds,
              `${TasksFilterFieldNames.Site}.value`,
            );
            const siteId = _get(values, siteFieldName) as string;
            if (siteId) return `${TasksFilterFieldNames.Site}-${siteId}`;
            return TasksFilterFieldNames.SiteAreaIds;
          },
          isOptionalFn: () => false,
          hideErrorsSpace: true,
          formItemClassName: formStyles.formColumn,
        },
      ],
    },
  ];
}
